import { api } from "@stoneflow/backend/convex/_generated/api"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { Building2, Plus, Users } from "lucide-react"
import { CreateTenantDialog } from "#/components/create-tenant-dialog"
import { RouteError } from "#/components/route-error"
import { Badge } from "#/components/ui/badge"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card"
import { Skeleton } from "#/components/ui/skeleton"
import { formatRelTime } from "#/lib/utils"

export const Route = createFileRoute("/_authenticated/admin/")({
	errorComponent: RouteError,
	component: AdminDashboard,
})

function AdminDashboard() {
	const tenants = useQuery(api.platform.tenants.list, {})

	if (tenants === undefined) {
		return <DashboardSkeleton />
	}

	const totalTenants = tenants.length
	const activeTrials = tenants.filter((t) => t.status === "trial").length
	const totalSeats = tenants.reduce((sum, t) => sum + t.seatLimit, 0)
	const recentOrgs = [...tenants].sort((a, b) => b.createdAt - a.createdAt).slice(0, 5)

	return (
		<div className="space-y-6">
			<div className="grid gap-4 sm:grid-cols-3">
				<Card size="sm">
					<CardContent className="flex items-center gap-3 pt-4">
						<Building2 className="size-5 text-foreground-muted" />
						<div>
							<p className="text-2xl font-medium">{totalTenants}</p>
							<p className="text-technical">Total Tenants</p>
						</div>
					</CardContent>
				</Card>
				<Card size="sm">
					<CardContent className="flex items-center gap-3 pt-4">
						<Badge variant="secondary">{activeTrials}</Badge>
						<div>
							<p className="text-technical">Active Trials</p>
						</div>
					</CardContent>
				</Card>
				<Card size="sm">
					<CardContent className="flex items-center gap-3 pt-4">
						<Users className="size-5 text-foreground-muted" />
						<div>
							<p className="text-2xl font-medium">{totalSeats}</p>
							<p className="text-technical">Total Seats</p>
						</div>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<div>
						<CardTitle>Recent Tenants</CardTitle>
						<CardDescription>Last 5 organizations created</CardDescription>
					</div>
					<CreateTenantDialog
						trigger={
							<Button size="sm">
								<Plus className="mr-1 size-4" />
								Create Tenant
							</Button>
						}
					/>
				</CardHeader>
				<CardContent>
					{recentOrgs.length === 0 ? (
						<p className="text-center text-sm text-foreground-secondary">
							No tenants yet. Create your first tenant to get started.
						</p>
					) : (
						<div className="space-y-3">
							{recentOrgs.map((t) => (
								<Link
									key={t.orgId}
									to="/admin/tenants/$orgId"
									params={{ orgId: t.orgId }}
									className="flex items-center justify-between rounded-md border border-border p-3 transition-colors hover:bg-accent/50"
								>
									<div className="min-w-0">
										<p className="truncate font-medium">{t.name}</p>
										<p className="text-xs text-foreground-muted">{formatRelTime(t.createdAt)}</p>
									</div>
									<Badge variant={t.status === "disabled" ? "destructive" : "secondary"}>
										{t.plan}
									</Badge>
								</Link>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	)
}

function DashboardSkeleton() {
	return (
		<div className="space-y-6">
			<div className="grid gap-4 sm:grid-cols-3">
				{Array.from({ length: 3 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
					<Card key={i} size="sm">
						<CardContent className="pt-4">
							<Skeleton className="h-8 w-20" />
							<Skeleton className="mt-1 h-3 w-24" />
						</CardContent>
					</Card>
				))}
			</div>
			<Card>
				<CardHeader>
					<Skeleton className="h-5 w-32" />
				</CardHeader>
				<CardContent className="space-y-3">
					{Array.from({ length: 3 }).map((_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
						<Skeleton key={i} className="h-14 w-full" />
					))}
				</CardContent>
			</Card>
		</div>
	)
}
